import React, { memo, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiChevronRight, FiDownload, FiPlus, FiRefreshCw, FiSearch, FiUsers } from 'react-icons/fi';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { allows, rightsOf, unitsOf, useSessionUser } from '../../lib/permissions';
import { timeAgo } from '../../lib/format';
import { BUTTON, CARD, EmptyState, INPUT, Notice, PageHeader, Pill, Segmented, Select, SIZE, SkeletonRows } from '../ui/kit';
import { STATUSES, ageLabel, initials, searchText, splitList, statusLabel, statusTone } from './patientModel';
import { patientAccess } from './api';
import { exportFiles } from '../fichiers/fileModel';

const PAGE_SIZE = 40;
const FILTERS_KEY = 'patients.filters';
const SORTS = [
  { value: 'recent', label: 'Mis à jour récemment' },
  { value: 'nom', label: 'Nom (A → Z)' },
  { value: 'dossier', label: 'N° de dossier' },
];
const AVATAR_TONE = {
  brand: 'bg-brand-50 text-brand-700',
  green: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-600',
  violet: 'bg-violet-50 text-violet-700',
  dark: 'bg-ink text-white',
  neutral: 'bg-black/[0.05] text-black/55',
};

const readFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTERS_KEY)) || {};
  } catch (error) {
    return {};
  }
};

const compare = {
  recent: (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
  nom: (a, b) => String(a.nom || '').localeCompare(String(b.nom || ''), 'fr', { sensitivity: 'base' }),
  dossier: (a, b) => String(a.dossierNumber || '').localeCompare(String(b.dossierNumber || ''), 'fr', { numeric: true }),
};

const PatientRow = memo(({ patient }) => {
  const meta = [patient.dossierNumber && `Dossier ${patient.dossierNumber}`, patient.lit, ageLabel(patient), patient.genre === 'Feminin' ? 'Féminin' : patient.genre]
    .filter(Boolean)
    .join(' · ');
  const diagnostics = splitList(patient.diagnostic);
  return (
    <li className="border-0 border-b-[1px] border-solid border-black/[0.05] last:border-b-0">
      <Link
        to={`/patients/${patient._id}`}
        className="flex items-center gap-[14px] px-[16px] py-[14px] text-ink no-underline transition hover:bg-black/[0.02] hover:text-ink focus-visible:bg-brand-50/60 focus-visible:outline-none sm:px-6"
      >
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-full text-[13px] font-semibold ${
            AVATAR_TONE[statusTone(patient.statut)] || AVATAR_TONE.neutral
          }`}
          aria-hidden="true"
        >
          {initials(patient.nom)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold">{patient.nom || 'Nom non précisé'}</span>
            {patient.urgence === 'oui' && <Pill tone="red">Urgence</Pill>}
          </span>
          <span className="mt-0.5 block truncate text-[13px] text-black/45">{meta || 'Informations non renseignées'}</span>
          {diagnostics.length > 0 && (
            <span className="mt-0.5 block truncate text-[12.5px] text-black/40">{diagnostics.join(' · ')}</span>
          )}
        </span>
        <span className="hidden flex-none items-center gap-1.5 md:flex">
          {patient.services && <Pill>{patient.services}</Pill>}
          {patient.validation === 'Non Validé' && <Pill tone="amber">Non validé</Pill>}
        </span>
        {patient.statut && <Pill tone={statusTone(patient.statut)}>{statusLabel(patient.statut)}</Pill>}
        <span className="hidden w-[118px] flex-none text-right text-[12.5px] text-black/40 lg:block">{timeAgo(patient.updatedAt)}</span>
        <FiChevronRight size={17} aria-hidden="true" className="flex-none text-black/25" />
      </Link>
    </li>
  );
});

const PatientsList = () => {
  const user = useSessionUser();
  const access = patientAccess(user);
  const { patients, ensurePatients, reloadPatients } = usePatientDoctorContext();
  const saved = useMemo(readFilters, []);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState(saved.search || '');
  const [unit, setUnit] = useState(saved.unit === undefined ? null : saved.unit);
  const [statut, setStatut] = useState(saved.statut || '');
  const [validation, setValidation] = useState(saved.validation || '');
  const [sort, setSort] = useState(saved.sort || 'recent');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState('');
  // Excel list of the patients' documents: Administrateur level, like the other exports.
  const canExportFiles = allows(rightsOf(user), 'fichiers', 'tout');

  const exportDocuments = async () => {
    setExporting(true);
    setExportNote('');
    try {
      const count = await exportFiles(user, patients);
      if (!count) setExportNote('Aucun fichier à exporter pour le moment.');
    } catch (error) {
      setExportNote('L’export des fichiers a échoué. Vérifiez la connexion, puis réessayez.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    ensurePatients()
      .then(() => !cancelled && setStatus('ready'))
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [ensurePatients]);

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ search, unit, statut, validation, sort }));
    } catch (error) {
      // Filters are only remembered when the browser allows it.
    }
  }, [search, unit, statut, validation, sort]);

  const refresh = () => {
    setStatus('loading');
    reloadPatients()
      .then(() => setStatus('ready'))
      .catch(() => setStatus('error'));
  };

  const unitsInData = useMemo(() => [...new Set(patients.map((patient) => patient.services).filter(Boolean))].sort(), [patients]);
  const myUnits = unitsOf(user.service);
  const defaultUnit = unitsInData.find((name) => myUnits.includes(name.toLowerCase())) || '';
  const activeUnit = unit === null ? defaultUnit : unit;

  // Search text built once per change of the list; the list follows the typing without
  // holding the keyboard back.
  const indexed = useMemo(() => patients.map((patient) => ({ patient, text: searchText(patient) })), [patients]);
  const query = useDeferredValue(search.trim().toLowerCase());
  const matching = useMemo(
    () =>
      indexed
        .filter(
          ({ patient, text }) =>
            (!activeUnit || patient.services === activeUnit) &&
            (!validation || patient.validation === validation) &&
            (!query || text.includes(query))
        )
        .map(({ patient }) => patient),
    [indexed, activeUnit, validation, query]
  );
  const counts = useMemo(() => {
    const result = {};
    matching.forEach((patient) => {
      result[patient.statut || ''] = (result[patient.statut || ''] || 0) + 1;
    });
    return result;
  }, [matching]);
  const shown = useMemo(
    () => matching.filter((patient) => !statut || patient.statut === statut).sort(compare[sort] || compare.recent),
    [matching, statut, sort]
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeUnit, validation, query, statut, sort]);

  const chip = (value, label, count) => {
    const active = statut === value;
    return (
      <button
        key={value || 'all'}
        type="button"
        aria-pressed={active}
        onClick={() => setStatut(value)}
        className={`inline-flex h-9 flex-none cursor-pointer items-center whitespace-nowrap gap-2 rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
          active ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
        }`}
      >
        {label}
        <span className={`tabular-nums ${active ? 'text-white/60' : 'text-black/35'}`}>{count || 0}</span>
      </button>
    );
  };

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Soins"
        title="Dossiers patients"
        subtitle={
          status === 'ready'
            ? `${shown.length} dossier${shown.length > 1 ? 's' : ''}${activeUnit ? ` · unité ${activeUnit}` : ' · toutes les unités'}`
            : 'Chargement des dossiers…'
        }
        actions={
          <>
            <button type="button" onClick={refresh} className={`${BUTTON.secondary} ${SIZE.md}`} disabled={status === 'loading'}>
              <FiRefreshCw size={15} aria-hidden="true" className={status === 'loading' ? 'animate-spin' : ''} />
              Actualiser
            </button>
            {canExportFiles && (
              <button type="button" onClick={exportDocuments} disabled={exporting || status !== 'ready'} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiDownload size={15} aria-hidden="true" />
                {exporting ? 'Export…' : 'Excel des fichiers'}
              </button>
            )}
            {access.canCreate && (
              <Link to="/patients/nouveau" className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiPlus size={16} aria-hidden="true" />
                Nouveau dossier
              </Link>
            )}
          </>
        }
      />

      {exportNote && (
        <div className="mt-6">
          <Notice tone="amber">{exportNote}</Notice>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="flex flex-col gap-[12px] sm:flex-row">
          <div className="relative flex-1">
            <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, n° de dossier, diagnostic, lit, opérateur…"
              aria-label="Rechercher un dossier"
              className={`${INPUT} h-11 pl-[42px]`}
            />
          </div>
          <div className="sm:w-[230px]">
            <Select value={sort} onChange={(e) => setSort(e.target.value || 'recent')} options={SORTS} placeholder="Trier par…" aria-label="Trier les dossiers" />
          </div>
        </div>

        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Segmented
              value={activeUnit}
              onChange={(value) => setUnit(value)}
              allowEmpty={false}
              options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]}
            />
          </div>
          <Segmented
            value={validation}
            onChange={(value) => setValidation(value)}
            allowEmpty={false}
            options={[
              { value: '', label: 'Tous' },
              { value: 'Validé', label: 'Validés' },
              { value: 'Non Validé', label: 'Non validés' },
            ]}
          />
        </div>

        <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
          {chip('', 'Tous', matching.length)}
          {STATUSES.map((item) => chip(item.value, item.label || item.value, counts[item.value]))}
        </div>
      </div>

      <section className={`${CARD} mt-[20px] overflow-hidden`}>
        {status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={6} />
          </div>
        ) : status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title="Liste indisponible" action={<button type="button" onClick={refresh} className={`${BUTTON.secondary} ${SIZE.md}`}>Réessayer</button>}>
            Les dossiers n’ont pas pu être chargés. Vérifiez la connexion, puis réessayez.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiUsers} title="Aucun dossier">
            {patients.length ? 'Aucun dossier ne correspond à ces filtres.' : 'Aucun dossier patient enregistré pour le moment.'}
          </EmptyState>
        ) : (
          <>
            <ul className="m-0 list-none p-0">
              {shown.slice(0, visible).map((patient) => (
                <PatientRow key={patient._id} patient={patient} />
              ))}
            </ul>
            {visible < shown.length && (
              <div className="border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setVisible((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - visible)} dossiers de plus
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default PatientsList;
